import {applyCurrentTheme} from "@/lib/utils.ts";
import {NotificationActionPerformedEvent, NotificationReceivedEvent} from "@capacitor-firebase/messaging";
import {Capacitor} from "@capacitor/core";
import {Badge} from '@capawesome/capacitor-badge';
import {HeroUIProvider} from "@heroui/react";
import {ToastProvider} from "@heroui/toast";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {NavigateOptions, Outlet, ToOptions, useLocation, useNavigate, useRouter} from '@tanstack/react-router';
import {getAnalytics, logEvent} from "firebase/analytics";
import Pusher from "pusher-js";
import React, {useEffect} from "react";
import {apiActionImproved} from "./api.ts";
import OutbrainPixel from "./components/pixels/OutbrainPixel.tsx";
import {config} from "./config/config.ts";
import {GlobalModalProvider} from "./core/GlobalModal.tsx";
import {PusherProvider} from "./core/PusherContext.tsx";
import {useJwtAuth, useJwtUser} from "./JWTAuthProvider.tsx";
import {adsGatedOn, pageAdsDecision, subscribePageAds} from "./ads/pageAds.ts";
import pushNotifications from "./mobile/push-notifications.ts";
import {App, URLOpenListenerEvent} from '@capacitor/app';

declare module "@react-types/shared" {
    interface RouterConfig {
        href: ToOptions['to'];
        routerOptions: Omit<NavigateOptions, keyof ToOptions>;
    }
}

/**
 * Routes that must never carry an ad. These are the paid-acquisition funnels: we buy the click,
 * and AdSense Auto ads were answering it with a full-screen vignette that covered the questions,
 * swallowed the tap on the first answer and rewrote the URL hash out from under the page.
 *
 * Gated here rather than in the AdSense account because Auto ads are a global account setting —
 * a URL-group exclusion there is invisible from this repo and silently undone by anyone editing
 * the account. Not loading the script at all is the only version that cannot regress.
 */
// The home page too: it is the front door, and an ad there sells the visitor away before they sign up.
const AD_FREE_PATHS = ['/', '/quiz', '/quiz-community', '/quiz-legacy', '/scorepal-quiz'];

const onAdFreeFunnel = (): boolean => {
    const path = document.location.pathname.replace(/\/+$/, '') || '/';

    return AD_FREE_PATHS.includes(path);
};

function MyPageViewLogger() {
    if (typeof window === 'undefined') {
        return null;
    }

    const analytics = getAnalytics();
    const location = useLocation();

    useEffect(() => {
        logEvent(analytics, 'page_view', {page_location: location.href});
    }, [location.href]);

    return null;
}

const AppThemed = ({children}) => {
    const jwt = useJwtAuth();
    const user = useJwtUser();
    const router = useRouter();
    const navigate = useNavigate();
    // A live view or registration page says whether its club bought ad-free pages once its event
    // has loaded (ads/pageAds.ts); the AdSense script waits for that answer on those paths.
    const pageAds = React.useSyncExternalStore(subscribePageAds, pageAdsDecision, () => null);

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries:   {
                        // With SSR, we usually want to set some default staleTime
                        // above 0 to avoid refetching immediately on the client
                        staleTime: 60 * 1000,
                    },
                    mutations: {
                        onError: (error: Error) => console.error('[MutationError]', error),
                    },
                },
            }),
    )

    // Disable websocket during SSR prerendering (pending connections prevent the build process from exiting)
    const pusher = typeof window !== 'undefined'
        ? new Pusher(config.pusherAppKey, {
            cluster: '',
            wsHost:  config.pusherHost,
            ...(config.pusherPort ? {wsPort: config.pusherPort, wssPort: config.pusherPort} : {}),
            forceTLS:             config.pusherForceTls,
            enableStats:          false,
            enabledTransports:    ['ws', 'wss'],
            userAuthentication:   {
                endpoint:  config.apiUrl + "/reverb-auth:user" + (typeof document !== 'undefined' && document.cookie.indexOf('XDEBUG_SESSION') > -1 ? config.debugParams : ''),
                transport: 'ajax'
            },
            channelAuthorization: {
                transport: 'ajax',
                endpoint:  config.apiUrl + "/reverb-auth" + (typeof document !== 'undefined' && document.cookie.indexOf('XDEBUG_SESSION') > -1 ? config.debugParams : ''),
                headers() {
                    return {
                        'x-firebase-auth': (user?.['stsTokenManager'])?.accessToken,
                    }
                },
            },
        })
        : null as unknown as Pusher;

    function handleNotificationActionPerformed(event: NotificationActionPerformedEvent) {
        console.log('notificationActionPerformed', JSON.stringify({event}));

        // const badgeCount = event.notification.data?.['badge'];
        // if (badgeCount) {
        //     Badge.set({count: badgeCount});
        // }

        const redirectUrl = event.notification.data?.['navigate-to'];
        if (redirectUrl) {
            navigate({to: redirectUrl})
        }
    }

    function handleNotificationReceived(event: NotificationReceivedEvent) {
        console.log('notificationReceived', JSON.stringify({event}));

        if (Capacitor.getPlatform() === 'android' && event.notification.data?.['badge']) {
            Badge.set({count: event.notification.data?.['badge']}) // no effect on Samsung Android
        }
        // pushNotifications.getDeliveredNotifications().then((res) => {
        //     console.log("pushNotifications.getDeliveredNotifications().then()", res)
        // })
    }

    useEffect(() => {
        if (!jwt.initialized) {
            return;
        }

        const scriptId = 'adsbygoogle-ca-pub-6471700087527589';
        // Its own effect: it re-runs when a gated page declares, and nothing else here should.
        const pageAllows = !adsGatedOn(document.location.pathname) || pageAds === true;
        if (pageAllows && document.location.hash !== '#no-tracking' && !onAdFreeFunnel() && !localStorage.disable_ads && !user?.is_subscribed && !user?.meta?.disable_ads && !document.getElementById(scriptId) && config.adsenseEnabled) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.async = true;
            script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6471700087527589";
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
        }
    }, [jwt.initialized, user, pageAds]);

    useEffect(() => {
        if (!jwt.initialized) {
            return;
        }

        document.querySelector('html')?.classList.toggle('user-subscribed', user?.is_subscribed === true);

        async function initFirebaseMessaging() {
            const permission = await pushNotifications.checkPermissions()
            if (permission === 'denied') {
                if (user?.meta?.firebase_messaging_permission !== 'denied') {
                    apiActionImproved('users-update', {
                        uid:    user!.uid,
                        values: {
                            version:                                 user!.version + 1,
                            ['meta->firebase_messaging_permission']: 'denied',
                        },
                    }).then(() => {
                        jwt.reloadUser();
                    });
                }
                return;
            }

            await pushNotifications.requestPermissions()
            const token = await pushNotifications.getToken()
            await pushNotifications.addNotificationReceivedListener(handleNotificationReceived)
            await pushNotifications.addNotificationActionPerformedListener(handleNotificationActionPerformed)
            await pushNotifications.getDeliveredNotifications()

            await pushNotifications.subscribeToTopic('all')
            await pushNotifications.subscribeToTopic(Capacitor.getPlatform())

            if (token && user?.uid) {
                if (localStorage.firebase_messaging_token !== token) {
                    localStorage.firebase_messaging_token = token;
                    apiActionImproved('users-update', {
                        uid:    user!.uid,
                        values: {
                            version:                            user!.version + 1,
                            ['meta->firebase_messaging_token']: token,
                        },
                    }).then(() => {
                        jwt.reloadUser();
                    });
                }
            }
        }

        if (config.isCordova) {
            initFirebaseMessaging();

            // Badge.clear(); // autoClear by config; on Samsung Android badge shows number of active notifications
        }

        return () => {
            pushNotifications.removeAllListeners();
        }

    }, [jwt.initialized, user]);

    function navigateToDeepLinkUrl(url: string) {
        if (!url) return;
        const path = url.split("scorepal.co").pop();
        if (path && path !== '/' && path !== '') {
            router.navigate({to: path as any});
        }
    }

    function enableDeepLinks() {
        // Warm-state: app is already running and OS hands us a new URL.
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            navigateToDeepLinkUrl(event.url);
        });
        App.getLaunchUrl().then((result) => {
            if (result?.url) {
                navigateToDeepLinkUrl(result.url);
            }
        }).catch(() => { /* not running in Capacitor */
        });
    }

    useEffect(() => {
        applyCurrentTheme();

        enableDeepLinks();
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <GlobalModalProvider>
                <PusherProvider pusher={pusher}>
                    <HeroUIProvider
                        navigate={(to, options) => router.navigate({to, ...options})}
                        useHref={(to) => router.buildLocation({to}).href}>
                        <ToastProvider
                            toastProps={{timeout: 3000, shouldShowTimeoutProgress: true,}}
                            placement="bottom-right"
                            toastOffset={70}
                        />
                        <OutbrainPixel />

                        {children}

                        {process.env.NODE_ENV === 'production' && <MyPageViewLogger />}
                    </HeroUIProvider>
                </PusherProvider>
            </GlobalModalProvider>

            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

export default AppThemed;
